/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "@redux/hooks";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTable, GenericTableColumn } from "@ui-components/Table/GenericTable";
import GenericButton from "@ui-components/Button/GenericButton";
import { ReactComponent as IconEdit } from "@shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconExpand } from "@shared/assets/Icons/IconExpand.svg";
import { getReflectorLicenses } from "../../Reflector/request/request";
import { useAuth0 } from "@auth0/auth0-react";
import EditLicenseModal from "./EditLicenseModal";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import DevicesModal from "../DevicesModal/DevicesModal";
import AddLicenseModal from "../LicenseModal/AddLicenseModal";

interface License {
	isExpired: boolean;
	usagePercentage: number;
	id: number;
	serial: string;
	description: string;
	activated: number;
	total: number;
	available: number;
	startDate: string;
	endDate: string;
	tenantId: number;
	type: string;
	status: number;
	createdAt: string;
	updatedAt: string;
	// Add devices field for super admin view
	devices?: string[];
	ReflectorInstances?: any[];
}

const PaidLicenses: React.FC = () => {
  const location = useLocation();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getAccessTokenSilently } = useAuth0();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [isDevicesModalOpen, setIsDevicesModalOpen] = useState(false);
  const [selectedInstances, setSelectedInstances] = useState<any[]>([]);
  const [isAddLicenseModalOpen, setIsAddLicenseModalOpen] = useState(false);
  const [prefillLicense, setPrefillLicense] = useState<License | null>(null);

  // Get user profile from Redux to check permissions
  const userProfile = useAppSelector((state) => state.userProfile);
  const tenantType = userProfile.TenantType;

  // Check if user is super admin and in admin center context
  const isSuperAdmin = tenantType === "GLOBAL_ADMIN";
  const isAdminCenter = location.pathname === "/reflector-v2/admin-center/management/licensing";
  const shouldShowAdminFeatures = isSuperAdmin && isAdminCenter;

  const fetchLicenses = async () => {
    try {
      setIsLoading(true);
      const token = await getAccessTokenSilently();
      const response = await getReflectorLicenses(token);
      
      // Add mock devices data for super admin view (replace with actual API call)
      const licensesWithDevices = response.data.data.map((license: License) => ({
        ...license,
        devices: shouldShowAdminFeatures ? [`Device-${license.id}-1`, `Device-${license.id}-2`] : undefined
      }));
      
      setLicenses(licensesWithDevices);
    } catch (err) {
      setError("Failed to fetch licenses. Please try again.");
      console.error("Error fetching licenses:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLicenses();
  }, [getAccessTokenSilently, shouldShowAdminFeatures]);

  const handleEdit = (license: License) => {
    if (shouldShowAdminFeatures) {
      setPrefillLicense(license);
      setIsAddLicenseModalOpen(true);
    } else {
      setSelectedLicense(license);
      setIsEditModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedLicense(null);
  };

  const handleLicenseUpdated = () => {
    fetchLicenses();
  };

  const handleAddLicense = () => {
    setIsAddLicenseModalOpen(true);
  };

  const handleCreateLicense = (data: any) => {
    // TODO: Call API to create license, then refresh
    setIsAddLicenseModalOpen(false);
    // fetchLicenses();
  };

  const handleDeviceAction = (licenseId: string, action: string) => {
    if (action === 'devices') {
      const license = licenses.find(l => String(l.id) === licenseId);
      setSelectedInstances(license?.ReflectorInstances || []);
      setIsDevicesModalOpen(true);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).replace(/\//g, '-');
  };

  // Build columns array conditionally
  const baseColumns: GenericTableColumn<License>[] = [
    { header: 'License', accessor: 'serial' },
    { header: 'Type', accessor: 'type' },
    { header: 'Description', accessor: 'description' },
    { header: 'Start date', accessor: 'startDate', renderCell: (row) => formatDate(row.startDate) },
    { header: 'End date', accessor: 'endDate', renderCell: (row) => formatDate(row.endDate) },
    { header: 'Activations', accessor: 'id', renderCell: (row) => `${row.activated} of ${row.total}` },
    { 
      header: 'Status', 
      accessor: 'status',
      renderCell: (row) => (
        <GenericTag label={!row.isExpired ? 'Active' : 'Expired'} variant={!row.isExpired ? 'success' : 'error'} />
      ) 
    },
    {
      header: "Actions",
      accessor: 'id',
      renderCell: (row) => (
        <div className="flex items-center gap-2">
          <GenericButton
            variant="secondary"
            size="sm"
            icon={<IconEdit className="w-8 h-8 text-semantic-text-secondary" />}
            isIconOnly
            onClick={() => handleEdit(row)}
            title="Edit license"
          />
          {shouldShowAdminFeatures && (
            <GenericButton
              variant="secondary"
              size="sm"
              icon={<IconExpand className="w-4 h-4 text-semantic-text-secondary" fill="currentColor" />}
              isIconOnly
              onClick={() => handleDeviceAction(String(row.id), 'devices')}
              title="Expand devices"
            />
          )}
        </div>
      ),
    },
  ];

  // Remove the conditional logic for admin features/devices column
  const columns: GenericTableColumn<License>[] = baseColumns;

  if (error) {
    return <div className="p-4 text-red-500">{error}</div>;
  }

  return (
    <>
      <div className="relative"> 
        <GenericCard isCollapsible={false} hasBorder={false} shadow="flat" >
          {/* Add License Button for Super Admin */}
          {shouldShowAdminFeatures && (
            <div className="flex justify-end p-4 border-b border-gray-100">
              <GenericButton
                label="Add License"
                variant="brand"
                size="md"
                onClick={handleAddLicense}
              />
            </div>
          )}
          <GenericTable
            columns={columns}
            data={licenses}
            initialRowsPerPage={10}
            rowsPerPageOptions={[5, 10, 20]}
            width="100%"
            tableClassName="bg-semantic-bg-primary"
          />
        </GenericCard>
      </div>
      {isLoading && (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-black/50 z-[9999]">
          <h2 className="text-xl font-semibold mb-4 text-white">
            Loading licenses...
          </h2>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-500" />
        </div>
      )}
      <EditLicenseModal
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        license={selectedLicense}
        onLicenseUpdated={handleLicenseUpdated}
      />
      <DevicesModal
        isOpen={isDevicesModalOpen}
        onClose={() => setIsDevicesModalOpen(false)}
        instances={selectedInstances}
      />
      <AddLicenseModal
        isOpen={isAddLicenseModalOpen}
        onClose={() => {
          setIsAddLicenseModalOpen(false);
          setPrefillLicense(null);
        }}
        onCreate={handleCreateLicense}
        licenseTypes={[
          { value: "CTXR-1Y-L", label: "CTXR-1Y-L" },
          { value: "CTXR-2Y-L", label: "CTXR-2Y-L" },
        ]}
        prefill={prefillLicense}
      />
    </>
  );
};

export default PaidLicenses;
